import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { ago } from "./format.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The card every provider on the API/AI Usage page is drawn in: Claude Max,
   Railway and MuleRouter from apps/web, HikerAPI from the Research module.
   It lives in the kit because a module's web half cannot import apps/web,
   and Jay asked (2026-10-01) for HikerAPI to look exactly like the others.
   ────────────────────────────────────────────────────────────────────────── */

/** Re-renders every so often, so "Updated 2 minutes ago" keeps telling the truth. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);
  return now;
}

/** The provider's mark in front of its name, in the text colour unless the mark has its own. */
export function ProviderName({ logo: Logo, children }: { logo?: ComponentType<{ size?: number }>; children: ReactNode }) {
  return (
    <span className="signet-usage-name">
      {Logo ? <span className="signet-usage-logo"><Logo size={16} /></span> : null}
      {children}
    </span>
  );
}

/** "Updated 3 minutes ago" with the clock time on hover. */
export function Updated({ at }: { at: string | number | undefined }) {
  const now = useNow();
  if (!at) return null;
  const when = new Date(at);
  return <span title={when.toLocaleString()}>Updated {ago(when, now)}</span>;
}

/** Green to amber at 75%, to red at 90%: the point to slow down, and the point to stop. */
export function toneFor(percent: number): "" | "warn" | "bad" {
  return percent >= 90 ? "bad" : percent >= 75 ? "warn" : "";
}

export function Meter({ label, percent, detail }: { label: ReactNode; percent: number; detail?: ReactNode }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="signet-meter">
      <div className="signet-meter-top">
        <span>{label}</span>
        <strong className="tabular">{Math.round(percent)}%</strong>
      </div>
      <span className="signet-usage-track" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped)} aria-label={typeof label === "string" ? label : undefined}>
        <span className={`signet-usage-fill ${toneFor(percent)}`.trim()} style={{ width: `${clamped}%` }} />
      </span>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

/** One provider's card: name and mark, a line on what it measures, actions on the right, the body, an optional foot. */
export function UsageCard({
  id,
  title,
  logo,
  caption,
  actions,
  foot,
  children,
}: {
  id: string;
  title: string;
  logo?: ComponentType<{ size?: number }>;
  caption: ReactNode;
  actions?: ReactNode;
  foot?: ReactNode;
  children: ReactNode;
}) {
  const headId = `signet-usage-${id.replace(/\W+/g, "-")}`;
  return (
    <section className="panel signet-usage-card" aria-labelledby={headId}>
      <div className="panel-head">
        <div>
          <h2 id={headId}><ProviderName logo={logo}>{title}</ProviderName></h2>
          <p>{caption}</p>
        </div>
        {actions ? <div className="panel-head-actions">{actions}</div> : null}
      </div>
      <div className="panel-body signet-usage-card-body">{children}</div>
      {foot ? <div className="panel-foot">{foot}</div> : null}
    </section>
  );
}
